import * as React from 'react';
import { cn } from '../../lib/utils';
import { Label } from './label';

/**
 * FormField component with exact Hyperkit styling
 *
 * Combines Label + Input + Hint/Error with proper spacing
 *
 * Hyperkit Specs:
 * - Label to Input gap: 8px (space-y-2)
 * - Hint: 12px/400 Body/XSmall, grey-500
 * - Error: 12px/400 Body/XSmall, red-600
 * - Error icon: Optional, 16px
 */

const FormField = React.forwardRef(
  (
    {
      className,
      label,
      htmlFor,
      required,
      hint,
      error,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <div ref={ref} className={cn('space-y-2', className)} {...props}>
        {label && (
          <Label htmlFor={htmlFor} required={required} error={!!error}>
            {label}
          </Label>
        )}
        {children}
        {(hint || error) && (
          <p
            className={cn(
              'text-xs leading-4', // Body/XSmall: 12px/16px
              error
                ? 'text-red-600 dark:text-red-400'
                : 'text-grey-500 dark:text-grey-400'
            )}
          >
            {error || hint}
          </p>
        )}
      </div>
    );
  }
);
FormField.displayName = 'FormField';

/**
 * Inline variant for horizontal form layouts
 */
const FormFieldInline = React.forwardRef(
  (
    {
      className,
      label,
      htmlFor,
      required,
      hint,
      error,
      labelWidth = 'w-32',
      children,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn('flex items-start gap-4', className)}
        {...props}
      >
        {label && (
          <Label
            htmlFor={htmlFor}
            required={required}
            error={!!error}
            className={cn('pt-2 shrink-0', labelWidth)}
          >
            {label}
          </Label>
        )}
        <div className="flex-1 space-y-1">
          {children}
          {(hint || error) && (
            <p
              className={cn(
                'text-xs leading-4',
                error
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-grey-500 dark:text-grey-400'
              )}
            >
              {error || hint}
            </p>
          )}
        </div>
      </div>
    );
  }
);
FormFieldInline.displayName = 'FormFieldInline';

export { FormField, FormFieldInline };
